import { useState } from "react";

const SPOTS = [
  ["crown", "Crown", 50, 7.5],
  ["brow", "Brow", 50, 11.5],
  ["throat", "Throat", 50, 18],
  ["heart", "Heart", 50, 26],
  ["solar", "Solar plexus", 50, 33.5],
  ["sacral", "Womb / sacral", 50, 43],
  ["root", "Root", 50, 51],
  ["hands", "Hands", 30.5, 55.5],
  ["hands", "Hands", 69.5, 55.5],
  ["thighs", "Thighs", 44, 62],
  ["feet", "Feet", 50, 92],
  ["jaw", "Jaw", 50, 14.5],
  ["shoulders", "Shoulders", 34, 20.5],
  ["shoulders", "Shoulders", 66, 20.5],
  ["hips", "Hips", 39, 45.5],
  ["hips", "Hips", 61, 45.5],
  ["knees", "Knees", 43.5, 72],
  ["knees", "Knees", 56.5, 72],
];
// Areas you can't see from the front, offered as buttons under the figure.
const BACK = [["neck_back", "Back of neck"], ["upper_back", "Upper back"], ["lower_back", "Lower back"]];
export const REGION_LABELS = Object.fromEntries(SPOTS.map(([k, l]) => [k, l]).concat(BACK, [["aura", "Whole field"]]));
const LEVELS = ["Nothing", "A flicker", "A current", "Radiant"];

const Glow = ({ v }) => {
  if (!v) return null;
  const size = 22 + v * 14;
  return <span aria-hidden className="absolute rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ width: size, height: size, left: "50%", top: "50%", background: `radial-gradient(circle, rgba(242,200,140,${0.35 + v * 0.18}) 0%, rgba(194,65,79,${0.25 + v * 0.15}) 45%, transparent 72%)` }} />;
};

// Tension glows cool and clouded, so it reads differently from the warm current.
const Knot = ({ v }) => (v ? <span aria-hidden className="absolute rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none border border-[#9DB4D6]/70" style={{ width: 16 + v * 12, height: 16 + v * 12, left: "50%", top: "50%", background: `radial-gradient(circle, rgba(157,180,214,${0.3 + v * 0.15}) 0%, rgba(90,110,150,${0.2 + v * 0.1}) 50%, transparent 75%)` }} /> : null);

// Tap where you feel the current (or, in Tension mode, where you hold tension). Each tap brightens a point.
export default function BodyMap({ figure, onFigure, value = {}, onChange, tension = {}, onTension, testId = "qi-body-map" }) {
  const [mode, setMode] = useState("current");
  const tense = mode === "tension" && onTension;
  const tap = (k) => (tense ? onTension({ ...tension, [k]: ((tension[k] ?? 0) + 1) % 4 }) : onChange?.({ ...value, [k]: ((value[k] ?? 0) + 1) % 4 }));
  const aura = value.aura ?? 0;
  return (
    <div data-testid={testId}>
      {onTension && (
        <div className="mb-3 grid grid-cols-2 gap-2" role="radiogroup" aria-label="What you are marking">
          {[["current", "Where I feel current"], ["tension", "Where I hold tension"]].map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={mode === k} data-testid={`${testId}-mode-${k}`} onClick={() => setMode(k)} className={`rounded-full border px-3 py-2 text-[12px] transition-colors ${mode === k ? (k === "tension" ? "bg-[#9DB4D6] border-[#9DB4D6] text-[#0A0706]" : "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]") : "border-[#D9B47A]/30 text-[#CFC2B6]"}`}>{l}</button>
          ))}
        </div>
      )}
      {onFigure && (
        <div className="flex gap-2" role="radiogroup" aria-label="Body">
          {[["female", "Female"], ["male", "Male"]].map(([k, l]) => (
            <button key={k} data-testid={`${testId}-figure-${k}`} role="radio" aria-checked={figure === k} onClick={() => onFigure(k)} className={`rounded-full border px-4 py-2 text-[13px] transition-colors ${figure === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/30 text-[#CFC2B6]"}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="relative mx-auto mt-4 w-full max-w-[300px] aspect-[848/1264] overflow-hidden rounded-sm" style={{ boxShadow: aura ? `0 0 ${20 + aura * 25}px rgba(217,180,122,${0.15 + aura * 0.12}) inset` : undefined }}>
        <img src={`/images/body-${figure}.webp`} alt={`${figure === "male" ? "Male" : "Female"} body outline`} className="absolute inset-0 w-full h-full object-cover select-none" draggable={false} />
        {SPOTS.map(([k, label, x, y], i) => (
          <button key={`${k}-${i}`} type="button" data-testid={`${testId}-spot-${k}${SPOTS.findIndex((s) => s[0] === k) < i ? "-r" : ""}`} disabled={!onChange && !onTension} onClick={() => tap(k)} aria-label={`${label}: ${LEVELS[Math.round(value[k] ?? 0)]}${tension[k] ? ", tension" : ""}`}
            className="absolute w-9 h-9 -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center disabled:cursor-default" style={{ left: `${x}%`, top: `${y}%` }}>
            <Glow v={value[k] ?? 0} />
            <Knot v={tension[k] ?? 0} />
            <span className={`relative w-2.5 h-2.5 rounded-full border ${value[k] ? "bg-[#F2E8DF] border-[#F2E8DF]" : "border-[#D9B47A]/70 bg-[#0B0509]/40"}`} />
          </button>
        ))}
      </div>
      {(onChange || onTension || BACK.some(([k]) => value[k] || tension[k])) && (
        <div className="mt-3 flex flex-wrap justify-center gap-2" aria-label="Back of the body">
          {BACK.map(([k, l]) => {
            const v = (tense ? tension[k] : value[k]) ?? 0;
            const t = tension[k] ?? 0;
            return <button key={k} type="button" data-testid={`${testId}-back-${k}`} disabled={!onChange && !onTension} onClick={() => tap(k)} className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors disabled:cursor-default ${t ? "border-[#9DB4D6] text-[#9DB4D6]" : value[k] ? "border-[#D9B47A] text-[#D9B47A]" : "border-[#D9B47A]/25 text-[#A8998C]"}`}>{l}{v ? ` · ${v}` : ""}</button>;
          })}
        </div>
      )}
      {onChange && (
        <div className="mt-4 flex items-center justify-between gap-3">
          <button type="button" data-testid={`${testId}-aura`} onClick={() => tap("aura")} className={`rounded-full border px-4 py-2 text-[13px] transition-colors ${aura ? "border-[#E8838E] text-[#E8838E]" : "border-[#D9B47A]/30 text-[#CFC2B6]"}`}>Whole field · {LEVELS[aura]}</button>
          <button type="button" data-testid={`${testId}-clear`} onClick={() => (tense ? onTension({}) : onChange({}))} className="text-[13px] text-[#A8998C] underline underline-offset-4">Clear</button>
        </div>
      )}
    </div>
  );
}
